// src/components/mdx/client/EmbedComponent.client.tsx

'use client'

import { type ReactNode } from 'react'
import { motion } from "motion/react"
import { cn } from '~/lib/utils'
import { InteractiveWrapper } from '~/components/blog/content/InteractiveWrapper'

interface EmbedComponentProps {
  /**
   * Raw embed markup rendered verbatim via `dangerouslySetInnerHTML`.
   *
   * SECURITY CONTRACT: this MUST be author-controlled, trusted HTML — i.e.
   * embed snippets committed to the repo's MDX content (typically an
   * `<iframe>`). It is NOT sanitized. Never pass user-submitted, remote, or
   * otherwise untrusted HTML here; route any looser source through a sanitizer
   * (e.g. DOMPurify) first.
   */
  html: string
  title?: string
  width?: number
  height?: number
  className?: string
}

interface EnhancedEmbedComponentProps extends EmbedComponentProps {
  provider?: string
  description?: string
  aspectRatio?: 'video' | 'square' | 'custom'
  customAspectRatio?: string
  footer?: ReactNode
  isInteractive?: boolean
}

// Simple version with basic functionality
export function EmbedComponent({
  html,
  title,
  width = 640,
  height = 360,
  className
}: EmbedComponentProps): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={cn("my-8", className)}
    >
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/50">
        {title && (
          <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              {title}
            </h3>
          </div>
        )}
        <div className="aspect-[16/9] w-full">
          <div 
            className="h-full w-full"
            dangerouslySetInnerHTML={{ __html: html }}
            style={{ 
              width: width ?? '100%',
              height: height ?? '100%'
            }}
          />
        </div>
      </div>
    </motion.div>
  )
}

// Enhanced version with more features and customization
export function EnhancedEmbedComponent({
  html,
  title,
  width = 640,
  height = 360,
  provider,
  description,
  aspectRatio = 'video',
  customAspectRatio,
  footer,
  isInteractive = true,
  className
}: EnhancedEmbedComponentProps): JSX.Element {
  const aspectRatioClass = {
    video: 'aspect-[16/9]',
    square: 'aspect-[1/1]',
    custom: customAspectRatio
  }

  const content = (
    <div className={cn("my-8", className)}>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-900/50">
        {/* Header */}
        {(title ?? provider) && (
          <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
            <div className="flex items-center justify-between">
              {title && (
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  {title}
                </h3>
              )}
              {provider && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {provider}
                </span>
              )}
            </div>
            {description && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {description}
              </p>
            )}
          </div>
        )}

        {/* Embed Content */}
        <div 
          className={cn(
            "w-full",
            aspectRatio === 'custom' ? customAspectRatio : aspectRatioClass[aspectRatio]
          )}
        >
          <div 
            className="h-full w-full"
            dangerouslySetInnerHTML={{ __html: html }}
            style={{ 
              width: width ?? '100%',
              height: height ?? '100%'
            }}
          />
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-800">
            {footer}
          </div>
        )}
      </div>
    </div>
  )

  return isInteractive ? (
    <InteractiveWrapper>
      {content}
    </InteractiveWrapper>
  ) : content
} 